import { PAGINATED_SIZE } from '@application/components/controllers/pages/LitterListPageController/constants';
import { isPromoItem, useSkipOnFirstRender } from '@application/utils';
import type { CardItemLitterData, StoreCardItemPromoData } from '@presentation/components';
import {
  BreedOverview,
  Button,
  CardItemBreeder,
  CardItemContainer,
  CardItemLitter,
  CardItemWrapper,
  Container,
  EmptyOverlay,
  FiltersWithModal,
  HeaderSearch,
  ListingQuizzCta,
  LoadMoreText,
  LoadMoreWrapper,
  PromoNewsletter,
  Section,
  StoreCardItemPromo,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import type { LitterListPageProps } from '..';
import { Filters } from './components/Filters';
import * as Styled from './styled';

export * from './types';

export const LitterListPage: FC<LitterListPageProps> = (props) => {
  const {
    items,
    newsletter,
    breederCategories,
    loading = false,
    loadMoreLoading = false,
    onLoadMoreClick,
    loadMoreText,
    canLoadMore = true,
    filtersModal,
    formOptions,
    pageTitle,
    breedOverview,
    breedInteretBanner,
    currentPage = 0,
  } = props;

  const { translate } = useTranslation();
  const canRender = useSkipOnFirstRender();

  return (
    <>
      <HeaderSearch title={pageTitle} />

      <Styled.Flipped>
        {breedOverview && (
          <Styled.OverviewContainer>
            <BreedOverview {...breedOverview} />
          </Styled.OverviewContainer>
        )}

        {canRender && (
          <Styled.FilterContainer>
            <FiltersWithModal {...filtersModal}>
              <Filters options={formOptions} />
            </FiltersWithModal>
          </Styled.FilterContainer>
        )}
      </Styled.Flipped>

      <Container>
        <ListingQuizzCta />
        {!loading && !items.length && (
          <EmptyOverlay wrapperProps={{ style: { minHeight: '200px' } }} />
        )}

        <CardItemContainer>
          {!!loading &&
            Array(PAGINATED_SIZE)
              .fill(undefined)
              .map((_, index) => (
                <CardItemWrapper key={index}>
                  <CardItemLitter loading breed="" price="" location="" />
                </CardItemWrapper>
              ))}
          {!loading &&
            items.map((item, index) => (
              <CardItemWrapper key={index}>
                {isPromoItem(index, PAGINATED_SIZE * (currentPage + 1)) ? (
                  <StoreCardItemPromo {...(item as StoreCardItemPromoData)} />
                ) : (
                  <CardItemLitter
                    {...(item as CardItemLitterData)}
                    useHeadingForTitle={!breedOverview}
                  />
                )}
              </CardItemWrapper>
            ))}
        </CardItemContainer>
        {canLoadMore && (
          <LoadMoreWrapper>
            <LoadMoreText>{loadMoreText}</LoadMoreText>
            <Button
              shrink
              color="blue"
              outlined
              loading={loadMoreLoading}
              onClick={onLoadMoreClick}
            >
              {translate('common.buttons.seeMore')}
            </Button>
          </LoadMoreWrapper>
        )}
      </Container>

      {breedInteretBanner && (
        <Section>
          <Container>{breedInteretBanner}</Container>
        </Section>
      )}

      {!!breederCategories?.length && (
        <>
          {breederCategories.map((category, index) => (
            <Styled.SectionWrapperBreeders key={index}>
              <Container>
                <Styled.SectionTitle color="blue">
                  {translate('common.sections.breedBreedings').replace('$breed', category.label)}
                </Styled.SectionTitle>
                <CardItemContainer>
                  {(category.breeders ?? []).map((breeder, breederIndex) => (
                    <CardItemWrapper key={breederIndex}>
                      <CardItemBreeder {...breeder} useHeadingForTitle={!breedOverview} />
                    </CardItemWrapper>
                  ))}
                </CardItemContainer>
                {category.canLoadMoreRelatedBreeders && (
                  <LoadMoreWrapper>
                    <Button shrink outlined color="blue" link={category.link}>
                      {translate('common.buttons.seeMore')}
                    </Button>
                  </LoadMoreWrapper>
                )}
              </Container>
            </Styled.SectionWrapperBreeders>
          ))}
        </>
      )}

      {!!newsletter && <PromoNewsletter {...newsletter} />}
    </>
  );
};
